import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CollapsibleSectionComponent } from '../../../shared/collapsible-section/collapsible-section.component';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

/**
 * Component for editing and validating basic campaign information (name and description) within a collapsible section.
 * @component
 * @selector app-basic-info
 * @example
 *   <app-basic-info [isExpanded]="true" [campaignData]="campaign" (toggle)="onToggle($event)" (done)="onDone()" (dataChange)="onDataChange($event)"></app-basic-info>
 * @input boolean isExpanded - Controls whether the basic info panel is expanded (default: true).
 * @input any campaignData - Initial campaign data object; expects { name?: string, description?: string } and is used to prefill the form.
 * @input boolean isCompleted - Indicates whether this step is marked completed by the parent; used as a read-only flag.
 * @output EventEmitter<boolean> toggle - Emitted when the user toggles the collapsible basic info section; payload is the new expanded state.
 * @output EventEmitter<void> done - Emitted when the user finishes this step and the form passes validation.
 * @output EventEmitter<any> dataChange - Emitted with the validated { name, description } payload when form fields change and pass validation.
 *
 * Purpose:
 *   Provides a self-contained UI for entering and validating the campaign name and description, enforcing rules such as required fields, minimum length, maximum length (300 chars), and allowed character sets. It filters disallowed characters on description input and surfaces conflict or API-provided errors.
 *
 * Key methods:
 *   - ngOnInit(): Initializes local form fields from campaignData when the component is created.
 *   - ngOnChanges(): Updates local form fields when campaignData input changes.
 *   - onBasicInfoToggle(expanded: boolean): Emits the toggle event to notify parent of expansion state changes.
 *   - onBasicInfoDone(): Runs validateForm() and emits done if there are no validation errors.
 *   - validateForm(): Sets validation flags for campaign name and description (required, min length, special characters, max length).
 *   - hasSpecialCharacters(text: string): Tests description for disallowed characters and returns true if invalid characters are present.
 *   - onDescriptionInput(event: any): Filters out disallowed characters from description input, manages length/min-length flags, and prevents invalid characters from remaining in the field.
 *   - onCampaignNameChange(): Validates the campaign name field, clears conflict and some description-related errors, and triggers emitDataChange().
 *   - onFormChange(): Validates both fields, updates error flags, and triggers emitDataChange().
 *   - emitDataChange(): Emits the dataChange event with { name, description } when all validations pass.
 *   - handleApiError(error: any): Allows parent to pass API errors (e.g., "Campaign already exists") to set a conflict error flag and message for display.
 *   - clearErrors(): Clears all validation and conflict-related flags and messages.
 *
 * Lifecycle hooks:
 *   - OnInit: Loads initial values from campaignData into internal form state.
 *   - OnChanges: Synchronizes internal form state whenever campaignData changes from the parent.
 */
@Component({
  selector: 'app-basic-info',
  imports: [CollapsibleSectionComponent, FormsModule, CommonModule],
  templateUrl: './basic-info.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './basic-info.component.scss',
})
export class BasicInfoComponent implements OnInit, OnChanges {
  @Input() isExpanded = true;
  @Input() campaignData: any = {};
  @Input() isCompleted = false;
  @Output() toggle = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();
  @Output() dataChange = new EventEmitter<any>();

  campaignName = '';
  description = '';
  showCampaignNameError = false;
  showCampaignNameMinLengthError = false;
  showCampaignNameSpecialCharError = false;
  showDescriptionError = false;
  showDescriptionSpecialCharError = false;
  showDescriptionLengthError = false;
  showDescriptionMinLengthError = false;
  campaignNameConflictError = false;
  campaignNameErrorMessage = '';

  ngOnInit(): void {
    this.syncFromCampaignData();
  }

  ngOnChanges(): void {
    this.syncFromCampaignData();
  }

  private syncFromCampaignData(): void {
    if (this.campaignData) {
      this.campaignName = this.campaignData.name || '';
      this.description = this.campaignData.description || '';
    }
  }

  onBasicInfoToggle(expanded: boolean): void {
    this.toggle.emit(expanded);
  }

  onBasicInfoDone(): void {
    this.validateForm();
    if (this.hasBlockingValidationErrors()) {
      return;
    }
    this.emitDataChange();
    this.done.emit();
  }

  validateForm(): void {
    this.showCampaignNameError = !this.campaignName || this.campaignName.trim() === '';
    this.showCampaignNameMinLengthError =
      this.campaignName.trim().length > 0 && this.campaignName.trim().length < 2;
    this.showCampaignNameSpecialCharError = this.hasSpecialCharacters(this.campaignName);
    this.showDescriptionError = !this.description || this.description.trim() === '';
    this.showDescriptionMinLengthError =
      this.description.trim().length > 0 && this.description.trim().length < 2;
    this.showDescriptionSpecialCharError = this.hasSpecialCharacters(this.description);
    this.showDescriptionLengthError = this.description.length > 300;

    if (!this.campaignName.trim()) {
      this.clearCampaignNameConflict();
    }
  }

  hasSpecialCharacters(text: string): boolean {
    const validPattern = /^[\p{L}\p{N}\s.,\-'()%]*$/u;
    return Boolean(text) && !validPattern.test(text);
  }

  private sanitizeCampaignText(input: string): string {
    return input.replace(/[^\p{L}\p{N}\s.,\-'()%]/gu, '');
  }

  /**
   * Sanitizes and validates the description input from an event, updating component state.
   * @example
   * nDescriptionInput({ target: { value: "Example description!" } })
   * undefined
   * @param {{any}} {{event}} - Input event from the description field; sanitizes special characters, trims and validates length, and updates the component's description and error flags.
   * @returns {{void}} No return value; updates component state (description and related validation flags).
   **/
  onDescriptionInput(event: any): void {
    const input = event.target.value;
    // Remove special characters on input
    const filtered = this.sanitizeCampaignText(input);
    if (input !== filtered) {
      this.description = filtered;
      event.target.value = filtered;
    }
    // Validate length
    this.showDescriptionLengthError = this.description.length > 300;
    this.showDescriptionMinLengthError =
      this.description.trim().length > 0 && this.description.trim().length < 2;
    // Clear required error if description has content
    if (this.description.trim() !== '') {
      this.showDescriptionError = false;
    }
  }

  /**
   * Handle campaign name input changes: reset conflict errors, validate name presence and minimum length, clear description-related errors, and emit a data change event.
   * @example
   * nCampaignNameChange()
   * undefined
   * @returns {{void}} No return value.
   **/
  onCampaignNameInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const input = target.value;
    const filtered = this.sanitizeCampaignText(input);
    const sanitized = input !== filtered;
    if (sanitized) {
      this.campaignName = filtered;
      target.value = filtered;
    }
    this.showCampaignNameSpecialCharError = this.hasSpecialCharacters(this.campaignName);
    this.showCampaignNameMinLengthError =
      this.campaignName.trim().length > 0 && this.campaignName.trim().length < 2;
    if (this.campaignName.trim() !== '') {
      this.showCampaignNameError = false;
    }
    if (sanitized) {
      this.onCampaignNameChange();
    }
  }

  onCampaignNameChange(): void {
    this.clearCampaignNameConflict();

    // Only validate campaign name field
    this.showCampaignNameError = !this.campaignName || this.campaignName.trim() === '';
    this.showCampaignNameMinLengthError =
      this.campaignName.trim().length > 0 && this.campaignName.trim().length < 2;
    this.showCampaignNameSpecialCharError = this.hasSpecialCharacters(this.campaignName);

    // Clear description errors when typing in campaign name
    this.showDescriptionError = false;
    this.showDescriptionSpecialCharError = false;

    this.emitDataChange();
  }

  /**
   * Updates validation state for the campaign name and description, clears any previous name-conflict errors, and emits a data-change event.
   * @example
   * nFormChange()
   * // returns void
   * @returns {void} No return value; side-effects include setting validation flags and calling emitDataChange().
   **/
  onFormChange(): void {
    this.clearCampaignNameConflict();

    // Validate both fields
    this.showCampaignNameError = !this.campaignName || this.campaignName.trim() === '';
    this.showCampaignNameMinLengthError =
      this.campaignName.trim().length > 0 && this.campaignName.trim().length < 2;
    this.showCampaignNameSpecialCharError = this.hasSpecialCharacters(this.campaignName);
    this.showDescriptionError = !this.description || this.description.trim() === '';
    this.showDescriptionMinLengthError =
      this.description.trim().length > 0 && this.description.trim().length < 2;
    this.showDescriptionSpecialCharError = this.hasSpecialCharacters(this.description);
    this.showDescriptionLengthError = this.description.length > 300;

    this.emitDataChange();
  }

  emitDataChange(): void {
    if (!this.hasBlockingValidationErrors()) {
      this.dataChange.emit({
        name: this.campaignName.trim(),
        description: this.description.trim(),
      });
    }
  }

  private hasBlockingValidationErrors(): boolean {
    return (
      this.campaignNameConflictError ||
      this.showCampaignNameError ||
      this.showCampaignNameMinLengthError ||
      this.showCampaignNameSpecialCharError ||
      this.showDescriptionError ||
      this.showDescriptionSpecialCharError ||
      this.showDescriptionLengthError ||
      this.showDescriptionMinLengthError
    );
  }

  // Method to handle API errors from parent component
  handleApiError(error: any, attemptedName?: string): void {
    const currentName = this.campaignName.trim();
    if (!currentName) {
      this.clearCampaignNameConflict();
      return;
    }

    if (attemptedName !== undefined && !this.isSameCampaignName(currentName, attemptedName)) {
      // Ignore stale API responses after the user changed or cleared the name.
      return;
    }

    const message = String(error?.message || '');
    if (message.toLowerCase().includes('campaign already exists')) {
      this.campaignNameConflictError = true;
      this.campaignNameErrorMessage = message;
      return;
    }

    this.clearCampaignNameConflict();
  }

  private clearCampaignNameConflict(): void {
    this.campaignNameConflictError = false;
    this.campaignNameErrorMessage = '';
  }

  private isSameCampaignName(left: string, right: string): boolean {
    return (
      left.trim().toLowerCase() ===
      String(right ?? '')
        .trim()
        .toLowerCase()
    );
  }

  // Method to clear errors
  clearErrors(): void {
    this.clearCampaignNameConflict();
    this.showCampaignNameError = false;
    this.showCampaignNameMinLengthError = false;
    this.showCampaignNameSpecialCharError = false;
    this.showDescriptionError = false;
    this.showDescriptionSpecialCharError = false;
    this.showDescriptionLengthError = false;
    this.showDescriptionMinLengthError = false;
  }
}
